// The video stage. Speaker view: whoever is talking, big, with everyone else in a strip above. Gallery: an even
// grid. A shared screen takes the stage (with the drawing layer) and people move to a strip on the right.
// Remounting it (window ↔ corner ↔ pop-out) only re-attaches tracks; the drawing board is kept in `boardRef`.
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Loader2, WifiOff } from 'lucide-react';
import { AnnotationCanvas } from '../AnnotationCanvas';
import { MediaEl, Tile, handOrder, sTrack } from './media';

const STROKE_COLORS = ['#F59E0B', '#EF4444', '#22C55E', '#38BDF8', '#E879F9', '#FB923C'];
export function colorFor(id) {
  let h = 0;
  for (const ch of String(id || '')) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return STROKE_COLORS[h % STROKE_COLORS.length];
}

function useBoard(dc, screen, boardRef, canvasRef) {
  useEffect(() => dc.onApp((ev) => {
    const m = ev?.data;
    if (!m?.mm) return;
    if (m.mm === 'draw' || m.mm === 'clear') canvasRef.current?.applyRemote(m.mm === 'clear' ? { t: 'clear' } : m.payload);
    else if (m.mm === 'state') canvasRef.current?.applyState(m.payload);
    else if (m.mm === 'req-state') {
      const me = dc.participantsNow()?.local;
      if (me && sTrack(me) && canvasRef.current) dc.send({ mm: 'state', payload: canvasRef.current.getState() }, ev.fromId);
    }
  }), [dc, canvasRef]);

  useEffect(() => {  // a fresh canvas: restore what this tab had, else ask the sharer
    if (!screen) return undefined;
    const canvas = canvasRef.current;
    if (boardRef.current) canvas?.applyState(boardRef.current);
    else dc.send({ mm: 'req-state' });
    return () => { boardRef.current = canvas?.getState() || null; };
  }, [screen, dc, boardRef, canvasRef]);
}

export function StageNotice({ phase, network }) {
  if (phase === 'joining') {
    return <div className="absolute inset-0 z-10 flex items-center justify-center text-[#8AA3BC]"><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Connecting…</div>;
  }
  if (phase === 'joined' && network.interrupted) {
    return (
      <div className="absolute left-1/2 top-3 z-20 flex -translate-x-1/2 items-center gap-1.5 rounded-full bg-amber-500 px-3 py-1 text-xs font-semibold text-black" data-testid="mm-reconnecting">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Connection lost, reconnecting…
      </div>
    );
  }
  if (phase === 'joined' && network.quality === 'very-low') {
    return (
      <div className="absolute left-3 top-3 z-20 flex items-center gap-1 rounded-full bg-black/60 px-2 py-0.5 text-[11px] text-amber-300" data-testid="mm-weak-network">
        <WifiOff className="h-3 w-3" /> Weak connection
      </div>
    );
  }
  return null;
}

// Columns for an even gallery that fits the stage: roughly square.
const colsFor = (n) => Math.max(1, Math.ceil(Math.sqrt(n)));

export default function CallStage({ dc, boardRef, annotate, canvasRef, view = 'speaker', compact }) {
  const { participants, local, activeSpeaker } = dc;
  const all = useMemo(() => Object.values(participants).filter(Boolean), [participants]);
  const hands = useMemo(() => handOrder(participants), [participants]);
  const sharer = all.find((p) => sTrack(p)) || null;
  const screen = sharer ? sTrack(sharer) : null;
  const fallbackRef = useRef(null);
  const ref = canvasRef || fallbackRef;
  useBoard(dc, screen, boardRef, ref);
  const sendStroke = useCallback((payload) => dc.send({ mm: 'draw', payload }), [dc]);
  const me = local?.session_id;
  const tile = (p, extra = {}) => (
    <Tile key={p.session_id || 'local'} p={p} isLocal={p.session_id === me} hand={hands.get(p.session_id)}
      speaking={p.session_id === activeSpeaker && !!p.audio && all.length > 1} {...extra} />
  );

  if (screen) {
    return (
      <>
        <div className="absolute inset-0 flex" data-testid="mm-screen-stage">
          <div className="relative min-w-0 flex-1">
            <MediaEl track={screen} muted className="h-full w-full object-contain" />
            <AnnotationCanvas ref={ref} active={annotate} color={colorFor(me)} onStroke={sendStroke} />
            <div className="absolute left-3 top-3 rounded-md bg-black/60 px-2 py-1 text-[11px] text-white backdrop-blur-md">
              {sharer?.user_name || 'Someone'} is sharing{annotate && ' · annotating'}
            </div>
          </div>
          {!compact && <div className="flex w-44 shrink-0 flex-col gap-2 overflow-y-auto p-2">{all.map((p) => tile(p, { small: true }))}</div>}
        </div>
        <StageNotice phase={dc.phase} network={dc.network} />
      </>
    );
  }

  const remote = all.filter((p) => p.session_id !== me);
  const main = all.find((p) => p.session_id === activeSpeaker && p.session_id !== me) || remote[0] || local;
  const strip = all.filter((p) => p !== main);

  return (
    <>
      {compact ? (  // the corner tile shows whoever is talking, clear of its title strip and toolbar
        <div className="absolute inset-x-2 bottom-[3.25rem] top-8" data-testid="mm-compact-stage">{main && tile(main, { small: true, fill: true })}</div>
      ) : view === 'gallery' ? (
        <div className="absolute inset-0 grid content-center gap-3 p-3" data-testid="mm-grid"
          style={{ gridTemplateColumns: `repeat(${colsFor(all.length)}, minmax(0, 1fr))` }}>
          {all.map((p) => tile(p))}
        </div>
      ) : (
        <div className="absolute inset-0 flex flex-col gap-2 p-3" data-testid="mm-speaker-view">
          {strip.length > 0 && (
            <div className="flex h-24 shrink-0 justify-center gap-2 overflow-x-auto" data-testid="mm-filmstrip">
              {strip.map((p) => <div key={p.session_id} className="h-full shrink-0" style={{ aspectRatio: '16 / 9' }}>{tile(p, { small: true, fill: true })}</div>)}
            </div>
          )}
          <div className="min-h-0 flex-1" data-testid="mm-speaker-main">{main && tile(main, { fill: true })}</div>
        </div>
      )}
      <StageNotice phase={dc.phase} network={dc.network} />
    </>
  );
}
